import { useCallback, useEffect, useRef, useState } from "react"
import { ConnectionState, DisconnectReason, RoomEvent } from "livekit-client"
import { CONNECT_OPTIONS } from "./roomOptions"
import { IS_WEB } from "../../shared/platform"
import { useInterval } from "../../shared/useInterval"

const TICK_MS = 2000
// Espacement de la n-ième tentative. Grâce de 6 s d'abord — laisser le SDK
// annoncer une reprise plutôt qu'un arrêt. Puis 15 s, assez court pour que
// l'utilisateur ne poireaute pas sur une panne passagère. Puis un plancher de
// 60 s : sur une panne longue, ou un incident SFU qui touche tout le monde en
// même temps, retenter indéfiniment toutes les 15 s ferait de nos clients une
// charge supplémentaire au pire moment. C'est ce plancher qui protège le
// serveur, avec le fait qu'un seul `connect()` soit en vol à la fois — pas un
// plafond d'essais, qui laisserait la room morte en silence.
const RETRY_DELAYS_MS = [6000, 15000, 15000, 15000]
const RETRY_FLOOR_MS = 60000

export const delayBeforeAttempt = (attempts) =>
    RETRY_DELAYS_MS[attempts] ?? RETRY_FLOOR_MS
// Au-delà, on propose « Réessayer ». Court exprès : l'utilisateur ne doit
// jamais se retrouver devant un écran qui tourne sans issue autre qu'« Annuler »,
// qui le fait *quitter* la séance. Pas nul pour autant — pendant la connexion
// initiale, normale et brève, le bouton n'aurait aucun sens.
const MANUAL_RETRY_AFTER_MS = 4000

/**
 * Faut-il lancer un `connect()` maintenant ? Extrait pour être testable seul.
 *
 * La seule règle qui compte : **ne jamais toucher à une reprise du SDK**. On
 * n'agit que sur `disconnected` ; `reconnecting`, `signalReconnecting` et
 * `connecting` lui appartiennent — c'est en marchant dessus que P-2502 cassait
 * les reconnexions.
 */
export const shouldAttemptReconnect = ({
    roomState,
    hasToken,
    connectInFlight,
    attempts,
    msSinceStopped,
    evictedByOtherClient,
    otherTabPresent,
}) =>
    roomState === ConnectionState.Disconnected &&
    hasToken &&
    !connectInFlight &&
    // Un autre client a pris notre identité (typiquement un second onglet).
    // Tant qu'il est là, se reconnecter la lui reprendrait, et il se
    // reconnecterait à son tour : mesuré, ça produit un ping-pong sans fin
    // toutes les ~8 s. C'est à `KeepTabModal` de trancher quel onglet garder.
    // Dès qu'on est seul — l'autre onglet est parti, ou l'utilisateur a choisi
    // celui-ci — le garde se lève, sinon l'onglet gagnant resterait mort.
    !(evictedByOtherClient && otherTabPresent) &&
    msSinceStopped >= delayBeforeAttempt(attempts)

/**
 * Dernier recours **web** : relance une room LiveKit que plus personne ne relance.
 *
 * Sur web, le seul pilote de connexion est `<LiveKitRoom connect>`, dont
 * l'effet dépend du token et des options — pas de l'état de connexion. Une room
 * qui meurt ne fait donc bouger aucune dépendance : l'effet ne se rejoue jamais
 * et la room reste morte jusqu'au rechargement de la page.
 *
 * On tourne sur une horloge plutôt que sur un changement d'état, pour la raison
 * qui avait piégé l'ancien code : un `connect()` raté laisse la room sur
 * `disconnected`, la valeur qu'elle avait déjà. Branché sur un *changement*,
 * plus rien ne se redéclenche. L'état est relu **à chaque tir**, jamais capturé.
 *
 * Pas de plafond d'essais : un budget épuisé laisserait la room morte en
 * silence, c'est-à-dire le bug qu'on corrige. Ce qui protège le SFU, c'est
 * l'espacement et le fait qu'un seul `connect()` soit en vol à la fois.
 *
 * **Entièrement inactif hors web**, conformément à son nom : le portail élève
 * natif a déjà son propre `connect()` gardé. L'écran tuteur natif, lui, n'a
 * aucun filet — c'est un manque réel, mais distinct, qui relève de la
 * reconnexion native (P-2768) et non de ce hook.
 *
 * @returns {{onRetry: (() => void) | undefined, isRetrying: boolean}}
 *   `onRetry` ne vaut une fonction que lorsque la reprise manuelle a un sens ;
 *   les appelants le passent tel quel à `ConnectingView`.
 */
export function useLiveKitWebReconnect(room, roomData, otherTabPresent = false) {
    const attemptsRef = useRef(0)
    const stoppedSinceRef = useRef(null)
    const inFlightRef = useRef(false)
    const evictedByOtherClientRef = useRef(false)
    // Doublé en ref pour n'appeler le setter que sur transition : le tick passe
    // toutes les 2 s pendant toute la séance, sur les deux écrans les plus
    // lourds de l'app. Redispatcher une valeur inchangée à ce rythme est du
    // rendu gratuit qu'on ne peut pas s'offrir ici.
    const canRetryRef = useRef(false)
    const [canRetryManually, setCanRetryManually] = useState(false)
    const [isRetrying, setIsRetrying] = useState(false)

    const setCanRetry = useCallback((next) => {
        if (canRetryRef.current === next) return
        canRetryRef.current = next
        setCanRetryManually(next)
    }, [])

    const connect = useCallback(() => {
        inFlightRef.current = true
        return room
            .connect(roomData?.serverUrl, roomData?.token, CONNECT_OPTIONS)
            .then(() => {
                stoppedSinceRef.current = null
            })
            .catch((reason) => {
                // On repart de l'instant de l'échec : l'écart suivant se compte
                // à partir de là, pas depuis l'arrêt initial.
                stoppedSinceRef.current = Date.now()
                console.log("WEB RECONNECT ERROR LIVEKIT", reason)
            })
            .finally(() => {
                inFlightRef.current = false
            })
    }, [room, roomData?.token, roomData?.serverUrl])

    /**
     * Un seul corps de décision, relu à chaque tir — par l'horloge comme par le
     * retour au premier plan. Dupliquer les gardes ailleurs ferait diverger les
     * deux chemins.
     */
    const attempt = useCallback(() => {
        if (!room) return

        if (room.state !== ConnectionState.Disconnected) {
            stoppedSinceRef.current = null
            attemptsRef.current = 0
            evictedByOtherClientRef.current = false
            setCanRetry(false)
            return
        }

        if (stoppedSinceRef.current === null) {
            stoppedSinceRef.current = Date.now()
            return
        }

        const msSinceStopped = Date.now() - stoppedSinceRef.current
        if (msSinceStopped >= MANUAL_RETRY_AFTER_MS) setCanRetry(true)

        const ready = shouldAttemptReconnect({
            roomState: room.state,
            hasToken: !!roomData?.token,
            connectInFlight: inFlightRef.current,
            attempts: attemptsRef.current,
            msSinceStopped,
            evictedByOtherClient: evictedByOtherClientRef.current,
            otherTabPresent,
        })
        if (!ready) return

        attemptsRef.current += 1
        connect()
    }, [room, roomData?.token, otherTabPresent, connect, setCanRetry])

    /**
     * Reprise déclenchée par l'utilisateur : elle court-circuite l'attente, et
     * passe outre l'éviction par un autre onglet — cliquer, c'est justement
     * choisir cet onglet-là.
     */
    const retryNow = useCallback(() => {
        if (!room || inFlightRef.current) return
        if (room.state !== ConnectionState.Disconnected) return
        attemptsRef.current = 0
        evictedByOtherClientRef.current = false
        setIsRetrying(true)
        connect().finally(() => setIsRetrying(false))
    }, [room, connect])

    // Retenir *pourquoi* la room s'est arrêtée. Effet séparé de l'horloge : il
    // ne dépend que de la room, donc l'abonnement survit aux changements
    // d'onglet au lieu d'être défait puis refait.
    useEffect(() => {
        if (!room || !IS_WEB) return undefined
        const onDisconnected = (reason) => {
            evictedByOtherClientRef.current =
                reason === DisconnectReason.DUPLICATE_IDENTITY
        }
        room.on(RoomEvent.Disconnected, onDisconnected)
        return () => room.off(RoomEvent.Disconnected, onDisconnected)
    }, [room])

    // `useInterval` garde le callback dans une ref : l'horloge n'est plus
    // redémarrée — ni son compteur remis à zéro — à chaque changement d'onglet
    // ou de token.
    useInterval(attempt, room && IS_WEB ? TICK_MS : null)

    // Une horloge ne suffit pas : ouvrir un second onglet met forcément le
    // premier en arrière-plan, où les navigateurs ralentissent fortement les
    // minuteries. On réévalue donc aussi au retour au premier plan, et
    // immédiatement au montage quand l'autre onglet vient de libérer la place.
    useEffect(() => {
        if (!room || !IS_WEB) return undefined
        attempt()
        document.addEventListener("visibilitychange", attempt)
        return () => document.removeEventListener("visibilitychange", attempt)
    }, [room, attempt])

    return {
        onRetry: canRetryManually ? retryNow : undefined,
        isRetrying,
    }
}
